import { isDefined } from "@clipboard-health/util-ts";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { differenceInMilliseconds, secondsToMilliseconds } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime } from "date-fns-tz";

import { formatTime } from "../../utils/formatTime";

export function formatShiftAuctionEndTime(parameters: { endTime: Date; timezone: string }): string {
  const { endTime, timezone } = parameters;
  const endTimeInTimezone = utcToZonedTime(endTime, timezone);
  return formatTime(endTimeInTimezone, timezone);
}

export function getMarks(parameters: {
  minimumRate: number;
  maximumRate: number;
  averageWinningBid?: number;
}) {
  const { minimumRate, maximumRate, averageWinningBid } = parameters;
  return [
    { value: minimumRate, label: `${formatDollarsAsUsd(minimumRate)}/hr` },
    ...(averageWinningBid ? [{ value: averageWinningBid, label: "|" }] : []),
    { value: maximumRate, label: `${formatDollarsAsUsd(maximumRate)}/hr` },
  ];
}

export function formatCountdownTime(secondsLeft: number): string {
  const secondsRemaining = Math.max(secondsLeft, 0);

  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = secondsRemaining % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * We have special handling to update the query interval once the auction ends.
 * If there is no auction, or the auction is not active anymore, we use the default refetch interval.
 *
 * This is designed to be called within the refetchInterval function, and
 * either the auctionEndTime or auctionStatusIsOpen should be determined by the query response so the refetch interval updates on each call.
 */
export function getRefetchIntervalForAuctionShiftInMilliseconds(parameters: {
  auctionEndTime: Date | undefined;
  auctionStatusIsOpen: boolean;
  defaultRefetchInterval?: number | false;
}): number | false {
  const { auctionEndTime, auctionStatusIsOpen, defaultRefetchInterval = false } = parameters;

  if (!isDefined(auctionEndTime) || !auctionStatusIsOpen) {
    return defaultRefetchInterval;
  }

  const millisecondsUntilAuctionEnd = differenceInMilliseconds(auctionEndTime, new Date());
  if (millisecondsUntilAuctionEnd > 0) {
    // Refetch three seconds after auction ends. (to give time for backend to update the auction status)
    return millisecondsUntilAuctionEnd + secondsToMilliseconds(3);
  }

  // If auction is still marked as open, and it's past the auction end time, refetch every 5 seconds.
  if (millisecondsUntilAuctionEnd >= secondsToMilliseconds(-30)) {
    return secondsToMilliseconds(5);
  }

  // If it is more than 30 seconds past the auction end time, there might be a delay in the auction status update, or some error.
  return defaultRefetchInterval;
}
